import { isEqual } from 'lodash';
import { useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import styled, { css } from 'styled-components';
import formatPhoneNumber from '../../../common/utils/formatPhoneNumber';
import { Address } from '../../../components/AddressSearchModal';
import AddressSearchModal from '../../../components/BrandSitesV2/OfficeInquire/AddressSearchModal';
import OfficeInquireAskResultModal from '../../../components/BrandSitesV2/OfficeInquire/OfficeInquireAskResultModal';
import PreferredTimeSelectModal from '../../../components/BrandSitesV2/OfficeInquire/PreferredTimeSelectModal';
import { apiClient } from '../../../lib/axios/apiClient';
import { Promotion } from '../../../pages/office_inquire/[promotionName]';
import PrivacyModal from '../../BrandSites/OfficeInquire/PrivacyModal';
import MarketingAgreeModal from './MarketingAgreeModal';
import { OFFICE_PRICE_CHECK_STORAGE_KEY, OfficePriceCheckStorageData } from '../../../pages/office_inquire';
import OfficeInquireNoOpenAlertModal from './OfficeInquireNoOpenAlertModal';
import { useRouter } from 'next/router';
import CompleteModal from '../../CompleteModal';

interface FormData {
  agentName: string;
  phoneNumber: string;
  companyName: string;
  industry: string;
  customIndustry: string;
  roadAddress: string;
  detailAddress: string;
  square: string;
  email: string;
  inquireContent: string;
  hasAgreedNecessary: boolean;
  hasAgreedMarketing: boolean;
}

export default function OfficeInquireAskContent({ promotion }: { promotion?: Promotion }) {
  const form = useForm<FormData>({ defaultValues: {} });
  const {
    register,
    handleSubmit,
    setValue,
    watch,
    getValues,
    setError,
    reset,
    formState: { errors, submitCount },
  } = form;

  const router = useRouter();

  const [address, setAddress] = useState<Address>();
  const [isAddressSearchModalVisible, setIsAddressSearchModalVisible] = useState(false);
  const isAgreeAllChecked = watch().hasAgreedNecessary && watch().hasAgreedMarketing;

  const [createdRecordId, setCreatedRecordId] = useState<number>();
  const [submittedForm, setSubmiitedForm] = useState<FormData | null>(null);
  const [isResultModalVisible, setIsResultModalVisible] = useState(false);
  const [isPreferredTimeSelectModalVisible, setIsPreferredTimeSelectModalVisible] = useState(false);
  const [isPrivacyModalVisible, setIsPrivacyModalVisible] = useState(false);
  const [isMarkingModalVisible, setIsMarketingModalVisible] = useState(false);
  const isFetching = useRef(false);

  const [modalAddress, setModalAddress] = useState<Address | undefined>();
  const [isCompleteModalVisible, setIsCompleteModalVisible] = useState(false);
  const [isNoOpenAlertModalVisible, setIsNoOpenAlertModalVisible] = useState(false);

  useEffect(() => {
    if (!!promotion?.name) return;

    // 견적확인 결과 봤다가, 상담하기로 온 경우 폼을 채워준다.
    try {
      const storageData: OfficePriceCheckStorageData = JSON.parse(
        window?.sessionStorage.getItem(OFFICE_PRICE_CHECK_STORAGE_KEY) as string,
      );

      setValue('phoneNumber', storageData?.phoneNumber);
      setValue('roadAddress', storageData?.address?.roadaddress);
      setAddress(storageData.address);
    } catch (error) {}
  }, [promotion]);

  useEffect(() => {
    if (address?.available_yn === false) {
      setModalAddress(address as Address);
      setAddress(undefined);
      setIsNoOpenAlertModalVisible(true);
      setValue('roadAddress', null as any, { shouldValidate: false });
    }
  }, [address]);

  return (
    <Container>
      <TopInfo>
        <Title>견적 상담 신청</Title>
        <Description>견적 상담을 신청하시면 빠른 시일 내에 담당자에게 연락드릴 수 있도록 하겠습니다.</Description>
        {!!promotion?.title ? (
          <PromotionBanner>
            <PromotionTitle>{promotion?.title}</PromotionTitle>
            <PromotionSubTitle>{promotion?.sub_title}</PromotionSubTitle>
            <PromotionContent>{promotion?.description}</PromotionContent>
            <PromotionBottomText>
              지금 앱에서 바로 예약 시 <span style={{ color: '#1A7C88' }}>정기 1회 무료 서비스</span> 제공
            </PromotionBottomText>
          </PromotionBanner>
        ) : (
          <DefaultPromotion>
            지금 앱에서 바로 예약 시 <span style={{ color: '#1A7C88' }}>정기 1회 무료 서비스</span> 제공
          </DefaultPromotion>
        )}
      </TopInfo>

      <Content>
        <form
          onSubmit={handleSubmit((data, event) => {
            if (isFetching.current) return;

            // 이미 똑같은 내용으로 보냈으면 안 보낸다
            if (createdRecordId && isEqual(submittedForm as Object, data)) {
              setIsPreferredTimeSelectModalVisible(true);
              return;
            }

            setSubmiitedForm({ ...data });

            const url = `${process.env.NEXT_PUBLIC_BASE_URL}/brand_site/office_estimate?c=lab`;

            isFetching.current = true;
            apiClient
              .post(url, {
                bcode: address?.bcode,
                zipCode: address?.zipcode,
                address: address?.oldaddress,
                roadAddress: address?.roadaddress,
                addressDetail: data.detailAddress?.trim(),
                buildNo: address?.build_no,
                contactName: data?.agentName.trim(),
                phone: data.phoneNumber.replace(/[^0-9]/g, ''),
                inquireText: data.inquireContent?.trim(),
                companyName: data.companyName?.trim(),
                square: data.square?.trim(),
                industry: data.industry === '기타' ? data.customIndustry : data.industry,
                promotion_name: promotion?.name,
                email: data?.email,
              })
              .then((res) => {
                const createdRecordId = res?.data?.id;
                setCreatedRecordId(createdRecordId);
                setIsPreferredTimeSelectModalVisible(true);

                // // @ts-ignore
                // window?.branch?.logEvent(BRANCH_EVENTS.RESERVE.key);
              })
              .catch((e) => {
                window.alert('제출 중 에러가 발생했습니다.');
              })
              .finally(() => {
                isFetching.current = false;
              });
          })}
        >
          <Field>
            <Label>담당자명</Label>
            <InputWrapper>
              <Input
                {...register('agentName', {
                  required: '담당자명을 입력해 주세요.',
                })}
                placeholder="담당자명을 입력해 주세요."
                $hasError={!!errors.agentName?.message}
                onInput={(e) => setValue('agentName', e.currentTarget.value.trimStart(), { shouldValidate: true })}
                maxLength={100}
              />
              <InputError>{errors.agentName?.message}</InputError>
            </InputWrapper>
          </Field>
          <Field>
            <Label>연락처</Label>
            <InputWrapper>
              <Input
                {...register('phoneNumber', {
                  required: '연락처를 입력해 주세요.',
                  minLength: { value: 10, message: '연락처를 정확히 입력해 주세요.' },
                  maxLength: { value: 13, message: '연락처를 정확히 입력해 주세요.' },
                })}
                inputMode="numeric"
                placeholder="견적 받을 담당자 연락처를 입력해주세요."
                $hasError={!!errors.phoneNumber?.message}
                onInput={(e) => {
                  // 첫번째 글자를 숫자 말고 다른 걸 쳤다면, 에러 띄우기
                  if (!watch().phoneNumber && /[^0-9]/g.test(e.currentTarget.value)) {
                    setError('phoneNumber', { type: 'custom', message: '숫자만 입력이 가능합니다.' });
                    setValue('phoneNumber', '');
                    return;
                  }

                  setValue('phoneNumber', formatPhoneNumber(e.currentTarget.value.trimStart().slice(0, 13)), {
                    shouldValidate: true,
                  });
                }}
                maxLength={13}
              />
              <InputError>{errors.phoneNumber?.message}</InputError>
            </InputWrapper>
          </Field>
          <Field>
            <Label>회사명</Label>
            <InputWrapper>
              <Input
                {...register('companyName', {
                  required: '회사명을 입력해 주세요.',
                })}
                placeholder="회사명을 입력해주세요."
                $hasError={!!errors.companyName?.message}
                onInput={(e) => setValue('companyName', e.currentTarget.value.trimStart(), { shouldValidate: true })}
                maxLength={100}
              />
              <InputError>{errors.companyName?.message}</InputError>
            </InputWrapper>
          </Field>

          <Field>
            <Label>업종</Label>
            <div style={{ display: 'flex', gap: 8 }}>
              <InputWrapper style={{ flex: '1 0 90px' }}>
                <SelectArrow>{SelectArrowSvg}</SelectArrow>
                <Select
                  {...register('industry', {
                    required: '업종을 선택해 주세요.',
                  })}
                  $isNotSelected={!watch().industry}
                  $hasError={!!errors.industry?.message}
                  defaultValue={''}
                >
                  <option value="" disabled style={{ display: 'none' }}>
                    업종을 선택해 주세요.
                  </option>
                  <option>일반 사무실</option>
                  <option>운동센터</option>
                  <option>학원</option>
                  <option>병원</option>
                  <option>기타</option>
                </Select>
                <InputError>{errors.industry?.message}</InputError>
              </InputWrapper>
              {watch().industry === '기타' && (
                <InputWrapper style={{ flex: '999 1 auto' }}>
                  <Input
                    {...register('customIndustry', {
                      required: '업종을 입력해 주세요.',
                    })}
                    placeholder="업종을 입력해주세요."
                    $hasError={!!errors.customIndustry?.message}
                    onInput={(e) =>
                      setValue('customIndustry', e.currentTarget.value.trimStart(), { shouldValidate: true })
                    }
                    maxLength={100}
                  />
                  <InputError>{errors.customIndustry?.message}</InputError>
                </InputWrapper>
              )}
            </div>
          </Field>

          <Field>
            <Label>사무실 주소</Label>
            <InputWrapper>
              <Input
                {...register('roadAddress', {
                  required: '사무실 주소를 입력해 주세요.',
                })}
                placeholder="사무실 주소를 입력해 주세요."
                $hasError={!!errors.roadAddress?.message}
                onClick={() => setIsAddressSearchModalVisible(true)}
                onKeyDown={() => setIsAddressSearchModalVisible(true)}
                style={{ cursor: 'pointer', marginBottom: 8 }}
                readOnly
              />
              <Input
                {...register('detailAddress', {
                  required: '상세 주소를 입력해 주세요.',
                })}
                placeholder="상세 주소를 입력해 주세요."
                $hasError={!!errors.detailAddress?.message}
                onInput={(e) => setValue('detailAddress', e.currentTarget.value.trimStart(), { shouldValidate: true })}
                maxLength={100}
              />
              <InputError>
                {(!!submitCount && !watch()?.roadAddress ? '사무실 주소를 입력해 주세요.' : '') ||
                  errors.detailAddress?.message}
              </InputError>
            </InputWrapper>
          </Field>
          <Field>
            <Label>사무실 평수</Label>
            <InputWrapper>
              <Input
                {...register('square', {
                  required: '사무실 평수를 입력해 주세요.',
                })}
                placeholder="사무실 평수를 입력해 주세요."
                $hasError={!!errors.square?.message}
                onInput={(e) => {
                  // 첫번째 글자를 숫자 말고 다른 걸 쳤다면, 에러 띄우기
                  if (!watch().square && /[^0-9]/g.test(e.currentTarget.value)) {
                    setError('square', { type: 'custom', message: '숫자만 입력이 가능합니다.' });
                    setValue('square', '');
                    return;
                  }

                  setValue('square', e.currentTarget.value.replace(/[^0-9]/g, ''), { shouldValidate: true });
                }}
                maxLength={100}
                inputMode="numeric"
              />
              <InputError>{errors.square?.message}</InputError>
            </InputWrapper>
          </Field>
          <Field>
            <Label>이메일</Label>
            <InputWrapper>
              <Input
                {...register('email', {
                  required: '이메일을 입력해 주세요.',
                  pattern: {
                    value:
                      /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
                    message: '이메일을 정확히 입력해 주세요.',
                  },
                })}
                placeholder="예: clean@cleaninglab.com"
                $hasError={!!errors.email?.message}
                onInput={(e) => setValue('email', e.currentTarget.value.trimStart(), { shouldValidate: true })}
                maxLength={100}
              />
              <InputError>{errors.email?.message}</InputError>
            </InputWrapper>
          </Field>
          <Field>
            <Label>문의내용</Label>
            <InputWrapper>
              <TextArea
                {...register('inquireContent', {
                  required: '문의내용을 입력해 주세요.',
                })}
                placeholder="문의하실 내용을 입력해 주세요."
                $hasError={!!errors.inquireContent?.message}
                onInput={(e) => setValue('inquireContent', e.currentTarget.value.trimStart(), { shouldValidate: true })}
                maxLength={1000}
              />
              <InputError>{errors.inquireContent?.message}</InputError>
            </InputWrapper>
          </Field>

          <SubmitArea>
            <Agrees>
              <Agree
                onClick={() => {
                  if (!isAgreeAllChecked) {
                    setValue('hasAgreedNecessary', true, { shouldValidate: true });
                    setValue('hasAgreedMarketing', true, { shouldValidate: true });
                  } else {
                    setValue('hasAgreedNecessary', false, { shouldValidate: true });
                    setValue('hasAgreedMarketing', false, { shouldValidate: true });
                  }
                }}
              >
                <AgreeCircle $hasError={!!submitCount && !watch().hasAgreedNecessary} $checked={isAgreeAllChecked}>
                  {AgreeCheckSvg}
                </AgreeCircle>
                <AgreeRight>
                  <AgreeMsg style={{ fontWeight: 600, color: '#262626' }}>모두 동의하기</AgreeMsg>
                </AgreeRight>
              </Agree>

              <Agree htmlFor="AGREE_NECESSARY">
                <input
                  {...register('hasAgreedNecessary', {
                    required: true,
                  })}
                  type="checkbox"
                  style={{ width: 0, height: 0, display: 'none' }}
                  id="AGREE_NECESSARY"
                />
                <AgreeCircle
                  $hasError={!!submitCount && !watch().hasAgreedNecessary}
                  $checked={watch().hasAgreedNecessary}
                >
                  {AgreeCheckSvg}
                </AgreeCircle>
                <AgreeRight>
                  <AgreeMsg>
                    <span>개인정보 수집 및 이용 동의 (필수)</span>
                    <AgreeArrow
                      onClick={(e) => {
                        e.preventDefault();
                        setIsPrivacyModalVisible(true);
                      }}
                    >
                      {RightArrow}
                    </AgreeArrow>
                  </AgreeMsg>
                </AgreeRight>
              </Agree>

              <Agree htmlFor="AGREE_MARKETING">
                <input
                  {...register('hasAgreedMarketing', {})}
                  type="checkbox"
                  style={{ width: 0, height: 0, display: 'none' }}
                  id="AGREE_MARKETING"
                />
                <AgreeCircle $checked={watch().hasAgreedMarketing}>{AgreeCheckSvg}</AgreeCircle>
                <AgreeRight>
                  <AgreeMsg>
                    <span>마케팅 정보 수신 동의 (선택)</span>
                    <AgreeArrow
                      onClick={(e) => {
                        e.preventDefault();
                        setIsMarketingModalVisible(true);
                      }}
                    >
                      {RightArrow}
                    </AgreeArrow>
                  </AgreeMsg>
                  <AgreeSubMsg>미선택 시 지역 오픈, 쿠폰 알림을 못 받을 수 있어요</AgreeSubMsg>
                </AgreeRight>
              </Agree>
            </Agrees>

            <Button>상담 신청하기</Button>
          </SubmitArea>
        </form>
      </Content>

      <PreferredTimeSelectModal
        visible={isPreferredTimeSelectModalVisible}
        setVisible={setIsPreferredTimeSelectModalVisible}
        createdRecordId={createdRecordId!}
        setResultModalVisible={setIsResultModalVisible}
      />
      <AddressSearchModal
        hasBodyScrollLock
        type="office"
        isVisible={isAddressSearchModalVisible}
        setIsVisible={setIsAddressSearchModalVisible}
        setAddress={
          ((address: Address) => {
            setValue('roadAddress', address.roadaddress, { shouldValidate: true });
            setAddress(address);
          }) as any
        }
      />
      <OfficeInquireNoOpenAlertModal
        isVisible={isNoOpenAlertModalVisible}
        setIsVisible={setIsNoOpenAlertModalVisible}
        prevPhoneNumber={watch()?.phoneNumber}
        address={modalAddress}
        setIsCompleteModalVisible={setIsCompleteModalVisible}
      />
      <CompleteModal
        visible={isCompleteModalVisible}
        title={'알림 신청 완료!'}
        handleClickOkay={() => router.push('/office_clean')}
      >
        해당 지역 서비스 오픈 시
        <br />
        등록된 연락처로 메시지 드리겠습니다.
      </CompleteModal>
      <OfficeInquireAskResultModal visible={isResultModalVisible} setVisible={setIsResultModalVisible} />
      <PrivacyModal isVisible={isPrivacyModalVisible} setIsVisible={setIsPrivacyModalVisible} />
      <MarketingAgreeModal isVisible={isMarkingModalVisible} setIsVisible={setIsMarketingModalVisible} />
    </Container>
  );
}

const Field = styled.div`
  display: flex;
  flex-direction: column;
  margin-bottom: 64px;
`;

const Label = styled.label`
  font-weight: 600;
  font-size: 19px;
  line-height: 140%;
  margin-bottom: 18px;
`;

const InputWrapper = styled.div`
  position: relative;
`;

const Input = styled.input<{ $hasError?: boolean }>`
  position: relative;
  box-shadow: ${({ $hasError }) => ($hasError ? '0 0 0 2px #ff5151' : 'none')};
  outline: none;
  border: none;
  border-radius: 8px;
  background: rgba(0, 0, 0, 0.03);
  width: 100%;
  font-family: inherit;
  font-size: 17px;
  font-weight: 400;
  padding: 14px 16px;
  color: rgba(0, 0, 0, 0.85);

  &::placeholder {
    color: rgba(0, 0, 0, 0.4);
  }

  appearance: none;
  &::-webkit-outer-spin-button,
  &::-webkit-inner-spin-button {
    appearance: none;
    margin: 0;
  }
`;

const Select = styled.select<{ $hasError?: boolean; $isNotSelected?: boolean }>`
  position: relative;
  box-shadow: ${({ $hasError }) => ($hasError ? '0 0 0 2px #ff5151' : 'none')};
  outline: none;
  border: none;
  border-radius: 8px;
  background: rgba(0, 0, 0, 0.03);
  width: 100%;
  font-family: inherit;
  font-size: 17px;
  font-weight: 400;
  padding: 14px 16px;
  color: ${({ $isNotSelected }) => ($isNotSelected ? '#999999' : 'rgba(0, 0, 0, 0.85)')};
  cursor: pointer;

  &::placeholder {
    color: rgba(0, 0, 0, 0.4);
  }

  appearance: none;
  &::-webkit-outer-spin-button,
  &::-webkit-inner-spin-button {
    appearance: none;
    margin: 0;
  }
`;

const SelectArrow = styled.div`
  position: absolute;
  right: 16px;
  top: 50%;
  transform: translateY(-50%);
  user-select: none;
  pointer-events: none;
`;

const InputError = styled.div<{ $top?: number }>`
  position: absolute;
  top: ${({ $top }) => `calc(100% + ${$top ?? 6}px)`};
  left: 4px;
  color: #f95353;
  font-size: 15px;
  line-height: 140%;
`;

const Container = styled.div`
  display: flex;
  padding: 132px 0;
  max-width: ${({ theme }) => theme.brandSitesV2!.containerWidths!.desktop};
  margin: 0 auto;

  @media (max-width: ${({ theme }) => theme.brandSitesV2!.breakpoints.tablet}) {
    max-width: ${({ theme }) => theme.brandSitesV2!.containerWidths!.tablet};
    flex-direction: column;
  }

  @media (max-width: ${({ theme }) => theme.brandSitesV2!.breakpoints.mobile}) {
    max-width: none;
    padding: 110px 24px 61px 24px;
  }
`;

const TopInfo = styled.div`
  /* background: red; */
  width: 415px;
  margin-right: 107px;
  flex-grow: 0;
  flex-shrink: 0;

  @media (max-width: ${({ theme }) => theme.brandSitesV2!.breakpoints.tablet}) {
    width: 100%;
    margin-right: 0px;
  }

  @media (max-width: ${({ theme }) => theme.brandSitesV2!.breakpoints.mobile}) {
    width: 100%;
  }
`;

const Title = styled.h1`
  font-weight: 700;
  font-size: 33px;
  line-height: 140%;
  color: #000000d9;
  margin-bottom: 32px;

  @media (max-width: ${({ theme }) => theme.brandSitesV2!.breakpoints.mobile}) {
    font-size: 23px;
    margin-bottom: 24px;
  }
`;

const Description = styled.h2`
  width: 100%;
  font-size: 19px;
  font-weight: 400;
  line-height: 26.6px;
  color: #00000099;
  margin-bottom: 24px;
  white-space: pre-line;

  @media (max-width: ${({ theme }) => theme.brandSitesV2!.breakpoints.mobile}) {
    font-size: 17px;
  }
`;

const PromotionBanner = styled.div`
  background: #e8f6f8;
  padding: 24px 20px;
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  line-height: 140%;
  color: #262626;
`;

const PromotionTitle = styled.p`
  font-weight: 700;
  font-size: 23px;
  margin-bottom: 8px;
  white-space: pre-line;
  line-height: 140%;
`;

const PromotionSubTitle = styled.p`
  font-weight: 600;
  font-size: 17px;
  margin-bottom: 24px;
  white-space: pre-line;
  line-height: 140%;
`;

const PromotionContent = styled.p`
  font-weight: 400;
  font-size: 17px;
  margin-bottom: 16px;
  color: #424b4c;
  white-space: pre-line;
  line-height: 140%;
`;

const PromotionBottomText = styled.p`
  border-top: 1px solid #0000000f;
  padding-top: 16px;
  font-weight: 600;
  font-size: 17px;
  line-height: 140%;
`;

const DefaultPromotion = styled.div`
  padding: 24px 20px;
  font-weight: 600;
  font-size: 17px;
  line-height: 140%;
  background: #e8f6f8;
  border-radius: 8px;
`;

const Button = styled.button`
  padding: 16px;
  border-radius: 8px;
  font-weight: 600;
  font-size: 17px;
  line-height: 140%;
  display: flex;
  justify-content: center;
  align-items: center;
  text-decoration: none;
  cursor: pointer;
  border: none;
  font-family: inherit;
  background: #00bed6;
  color: #ffffff;
  transition: 0.2s;
  user-select: none;

  &:disabled {
    background: rgba(0, 0, 0, 0.05);
    color: rgba(0, 0, 0, 0.2);
  }

  &:not(:disabled):hover {
    background: #19c4da;
  }
`;

const Content = styled.div`
  padding-top: 8px;
  flex-grow: 1;

  @media (max-width: ${({ theme }) => theme.brandSitesV2!.breakpoints.tablet}) {
    padding-top: 64px;
  }
`;

const SubmitArea = styled.div`
  padding-top: 24px;
  padding-bottom: 24px;
  display: flex;
  flex-direction: column;
`;

const Agrees = styled.div`
  margin-bottom: 20px;
  display: flex;
  flex-direction: column;
  user-select: none;
`;

const Agree = styled.label`
  position: relative;
  margin-bottom: 12px;
  display: flex;
  flex-direction: row;
  align-items: center;
  cursor: pointer;
  width: 100%;
`;

const AgreeArrow = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;
`;

const AgreeCircle = styled.div<{ $checked: boolean; $hasError?: boolean }>`
  width: 20px;
  height: 20px;
  border-radius: 50%;
  border: 2px solid #cccccc;
  margin-right: 8px;
  align-self: flex-start;
  display: flex;
  justify-content: center;
  align-items: center;

  ${({ $checked }) =>
    $checked &&
    css`
      background: #00bed6;
      border-color: #00bed6;
    `}

  ${({ $hasError }) =>
    $hasError &&
    css`
      border-color: #f95353;
    `}
`;

const AgreeRight = styled.div`
  display: flex;
  flex-direction: column;
  flex-grow: 1;
`;

const AgreeMsg = styled.div`
  color: #666666;
  font-size: 15px;
  line-height: 140%;

  width: 100%;
  display: flex;
  justify-content: space-between;
`;

const AgreeSubMsg = styled.p`
  margin-top: 4px;
  color: #999999;
  font-size: 15px;
  line-height: 140%;
`;

const AgreeCheckSvg = (
  <svg width="10" height="11" viewBox="0 0 10 11" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M1.25 6.25L3.65 8.5L9.25 2.5"
      stroke="white"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const TextArea = styled.textarea<{ $hasError?: boolean }>`
  outline: none;
  border: 2px solid ${({ $hasError }) => ($hasError ? '#ff5151' : 'rgba(0, 0, 0, 0)')};
  border-radius: 8px;
  background: rgba(0, 0, 0, 0.03);
  width: 100%;
  font-size: 17px;
  font-weight: 400;
  padding: 14px;
  color: rgba(0, 0, 0, 0.85);
  display: block;
  min-height: 100px;
  resize: none;

  /* &::placeholder {
    color: #999999;
  } */
`;

const SelectArrowSvg = (
  <svg width="16" height="10" viewBox="0 0 16 10" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M15.4992 1.06761C15.772 1.34159 15.771 1.7848 15.497 2.05756L8.52772 8.99588C8.25461 9.26778 7.81309 9.26778 7.53998 8.99588L0.508754 1.99588C0.234778 1.72312 0.233792 1.27991 0.506551 1.00593C0.779308 0.731958 1.22252 0.730972 1.4965 1.00373L8.03385 7.51205L14.5093 1.06541C14.7832 0.79265 15.2264 0.793636 15.4992 1.06761Z"
      fill="#CCCCCC"
    />
  </svg>
);

const RightArrow = (
  <svg width="16" height="17" viewBox="0 0 16 17" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M5.54705 3.00374C5.82102 2.73098 6.26423 2.73195 6.537 3.00592L11.4929 7.9838C11.7649 8.25691 11.7649 8.69845 11.4929 8.97156L6.49294 13.9937C6.22018 14.2677 5.77696 14.2686 5.50299 13.9959C5.22902 13.7231 5.22805 13.2799 5.50081 13.0059L10.0091 8.47768L5.54486 3.99369C5.2721 3.71972 5.27308 3.2765 5.54705 3.00374Z"
      fill="#CCCCCC"
    />
  </svg>
);
